import React, { useEffect, useRef } from "react";
import * as Tone from "tone";
import engine from "../../audio/AudioEngine";
import { useDAWStore } from "../../store/dawStore";

// Convert a dB value (typically -60..+6) into 0..1 normalized for a meter bar.
const dbToNorm = (db, floor = -60, ceil = 6) => {
  if (!Number.isFinite(db)) return 0;
  return Math.max(0, Math.min(1, (db - floor) / (ceil - floor)));
};

/**
 * Live track-level meter — vertical bar with green→yellow→red color stops and a
 * peak-hold tick. Polls engine.getTrackLevelDb at requestAnimationFrame cadence.
 */
export function TrackMeter({ trackId, height = 160, width = 12, vertical = true }) {
  const canvasRef = useRef(null);
  const peakRef = useRef({ value: 0, decay: 0 });
  const rafRef = useRef(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    // v19.16 P0 — Aggressive throttle to protect audio scheduling.
    //
    // Previously this meter painted at 60 fps unrestricted. With 10
    // tracks × 60 canvas paints/s × Tone.analyser reads, the main
    // thread was blocking the Tone.js lookAhead loop long enough to
    // starve the audio callback → the stutter + "corrupted waveform"
    // the user reported.
    //
    // New: cap at 30 fps normally, drop to 15 fps while the transport
    // is playing (visualizer smoothness matters far less than audio
    // clean-ness in that moment), and skip the frame entirely when the
    // tab is hidden.
    let lastFrameMs = 0;
    const draw = () => {
      const now = performance.now();
      const isPlaying = (() => { try { return Tone.getTransport && Tone.getTransport().state === "started"; } catch { return false; } })();
      const FRAME_MS = isPlaying ? 66 : 33; // 15 fps mid-playback / 30 fps idle.
      if (now - lastFrameMs < FRAME_MS) {
        rafRef.current = requestAnimationFrame(draw);
        return;
      }
      lastFrameMs = now;
      if (typeof document !== "undefined" && document.hidden) {
        rafRef.current = requestAnimationFrame(draw);
        return;
      }
      const w = canvas.width = canvas.clientWidth;
      const h = canvas.height = canvas.clientHeight;
      ctx.fillStyle = "#050505";
      ctx.fillRect(0, 0, w, h);
      const db = engine.getTrackLevelDb?.(trackId);
      const n = dbToNorm(db);
      const peak = peakRef.current;
      if (n >= peak.value) { peak.value = n; peak.decay = 0; }
      else { peak.decay += 1; if (peak.decay > 30) peak.value = Math.max(n, peak.value - 0.005); }
      const barH = n * h;
      const grad = ctx.createLinearGradient(0, h, 0, 0);
      grad.addColorStop(0,    "#10b981");
      grad.addColorStop(0.7,  "#22c55e");
      grad.addColorStop(0.85, "#eab308");
      grad.addColorStop(1,    "#ef4444");
      ctx.fillStyle = grad;
      ctx.fillRect(0, h - barH, w, barH);
      if (peak.value > 0.02) {
        const py = h - peak.value * h;
        ctx.fillStyle = "#ffffff";
        ctx.fillRect(0, py - 1, w, 1.5);
      }
      rafRef.current = requestAnimationFrame(draw);
    };
    rafRef.current = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(rafRef.current);
  }, [trackId, vertical]);

  return (
    <div
      className="rounded-sm overflow-hidden border border-[#1a1a1a]"
      style={{ width: vertical ? width : "100%", height, background: "#050505" }}
      data-testid={`track-meter-${trackId}`}
    >
      <canvas ref={canvasRef} style={{ display: "block", width: "100%", height: "100%" }} />
    </div>
  );
}

/**
 * Master output waveform + numeric dB readout — used by the standalone "Wave View"
 * plugin. Both the wave (from engine.masterWaveform) and the level (from
 * engine.masterMeter) are tapped off the masterGain node so the visualisation
 * always reflects the FINAL mixed signal.
 */
export function MasterWaveView({ height = 110 }) {
  const canvasRef = useRef(null);
  const readoutRef = useRef(null);
  const barRef = useRef(null);
  const rafRef = useRef(0);
  const stateRef = useRef({
    buf: null,       // Float32Array of peak-level samples, length = canvas.width
    lastBlock: -1,   // 8-bar block index — increments each time we cross a boundary
    lastX: -1,       // last written x, used to fill gaps + clear future cells on reset
  });
  const bpm = useDAWStore((s) => s.bpm);

  useEffect(() => {
    let lastFrameMs = 0;
    // v19.14 P0 — Throttle the wave-view repaint. Was previously firing
    // every rAF (≥60 fps) and dominated the main thread whenever the
    // audio scheduler needed to refill its lookAhead buffer.
    //
    // v19.16 P0 — Even more aggressive: 24 fps at idle, DROP to 12 fps
    // when the transport is playing. Waveform smoothness matters far
    // less than audio cleanness during playback — the corrupt/overlap
    // artefacts the user reported were the master analyser being read
    // and repainted in the same frame the Tone scheduler needed.
    const draw = () => {
      const now = performance.now();
      const isPlaying = (() => { try { return Tone.getTransport && Tone.getTransport().state === "started"; } catch { return false; } })();
      const FRAME_MS = isPlaying ? 83 : 42; // 12 fps mid-playback / 24 fps idle.
      if (now - lastFrameMs < FRAME_MS) {
        rafRef.current = requestAnimationFrame(draw);
        return;
      }
      lastFrameMs = now;
      // If the browser tab is hidden, skip the paint entirely — no
      // canvas work happens while the user isn't looking.
      if (typeof document !== "undefined" && document.hidden) {
        rafRef.current = requestAnimationFrame(draw);
        return;
      }
      const c = canvasRef.current;
      if (c) {
        const ctx = c.getContext("2d");
        const w = c.width = c.clientWidth;
        const h = c.height = c.clientHeight;

        // Ensure the peak-level buffer matches canvas width
        const st = stateRef.current;
        if (!st.buf || st.buf.length !== w) {
          st.buf = new Float32Array(w);
          st.lastBlock = -1;
          st.lastX = -1;
        }

        // 8-bar rolling window duration in seconds. When Transport crosses an
        // 8-bar boundary the block index increments and we clear the buffer
        // so a fresh 8-bar shape draws left→right — the requested "refresh
        // to show the next 8-bar block" behaviour.
        const secPerBar = (60 / (bpm || 120)) * 4;
        const windowSec = 8 * secPerBar;
        const nowMs = performance.now();
        const tSec = nowMs / 1000;
        const posInWindow = (tSec % windowSec) / windowSec; // 0..1
        const currentBlock = Math.floor(tSec / windowSec);
        if (currentBlock !== st.lastBlock) {
          // New 8-bar block — clear the buffer for a fresh sweep.
          st.buf.fill(0);
          st.lastBlock = currentBlock;
          st.lastX = -1;
        }

        // Sample the current master peak level (absolute of waveform tap) and
        // write it into the circular buffer at the current x column. Fill any
        // gap between lastX and x with the same value so a fast-drawing frame
        // doesn't leave holes.
        const data = engine.getMasterWaveform?.();
        let peak = 0;
        if (data) {
          for (let i = 0; i < data.length; i++) {
            const a = Math.abs(data[i]);
            if (a > peak) peak = a;
          }
        }
        const x = Math.min(w - 1, Math.max(0, Math.floor(posInWindow * w)));
        if (st.lastX < 0 || x < st.lastX) {
          st.buf[x] = peak;
        } else {
          for (let i = st.lastX; i <= x; i++) st.buf[i] = peak;
        }
        st.lastX = x;

        // Background
        ctx.fillStyle = "#050505";
        ctx.fillRect(0, 0, w, h);

        // v19.13 — Wave View cleanup. All overlays (bar-boundary vertical
        // guides, zero-line, red attack markers, progress cursor) are
        // removed so ONLY the audio waveform paints on the canvas. Per
        // user request: no visual obstructions of any kind.

        // Draw the accumulated 8-bar shape as a mirrored envelope.
        const buf = st.buf;
        // Base envelope
        const grad = ctx.createLinearGradient(0, 0, w, 0);
        grad.addColorStop(0,   "#22d3ee");
        grad.addColorStop(0.5, "#a855f7");
        grad.addColorStop(1,   "#ec4899");
        ctx.fillStyle = grad;
        ctx.beginPath();
        ctx.moveTo(0, h / 2);
        for (let i = 0; i < buf.length; i++) {
          const v = Math.min(1, buf[i]) * (h / 2) * 0.9;
          ctx.lineTo(i, h / 2 - v);
        }
        for (let i = buf.length - 1; i >= 0; i--) {
          const v = Math.min(1, buf[i]) * (h / 2) * 0.9;
          ctx.lineTo(i, h / 2 + v);
        }
        ctx.closePath();
        ctx.globalAlpha = 0.85;
        ctx.fill();
        ctx.globalAlpha = 1;
      }
      const db = engine.getMasterLevelDb?.();
      if (readoutRef.current) {
        readoutRef.current.textContent = Number.isFinite(db) ? `${db.toFixed(1)} dB` : "−∞ dB";
      }
      if (barRef.current) {
        const n = dbToNorm(db);
        barRef.current.style.width = `${(n * 100).toFixed(1)}%`;
        if (n > 0.85)      barRef.current.style.background = "linear-gradient(90deg,#10b981,#eab308,#ef4444)";
        else if (n > 0.6)  barRef.current.style.background = "linear-gradient(90deg,#10b981,#eab308)";
        else               barRef.current.style.background = "linear-gradient(90deg,#10b981,#22c55e)";
      }
      rafRef.current = requestAnimationFrame(draw);
    };
    rafRef.current = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(rafRef.current);
  }, [bpm]);

  const barsSec = (8 * (60 / (bpm || 120)) * 4).toFixed(1);
  return (
    <div className="space-y-2" data-testid="wave-view-master">
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-wider font-mono text-white/40" data-testid="wave-view-title">
          <span className="text-emerald-400 font-bold">MASTER</span> · 8-bar rolling ({barsSec}s @ {bpm} BPM)
        </div>
        <div ref={readoutRef} className="text-[11px] font-mono text-emerald-300" data-testid="wave-view-db">−∞ dB</div>
      </div>
      <div className="rounded-md overflow-hidden border border-[#1a1a1a]" style={{ height, background: "#050505" }}>
        <canvas ref={canvasRef} data-testid="wave-view-canvas" style={{ display: "block", width: "100%", height: "100%" }} />
      </div>
      <div className="h-2 rounded-full bg-[#0a0a0a] border border-[#1a1a1a] overflow-hidden">
        <div ref={barRef} style={{ width: "0%", height: "100%", transition: "background 120ms" }} data-testid="wave-view-bar" />
      </div>
    </div>
  );
}
